import { Capacitor } from '@capacitor/core';
import { authAPI, notificationAPI } from './api';

/**
 * Notification Service - Manages push notification permissions
 * Supports both mobile (Capacitor) and web (Notifications API)
 */

const NOTIFICATION_PERMISSION_KEY = 'rtb_notification_permission_checked';
const NOTIFICATION_PERMISSION_GRANTED_KEY = 'rtb_notification_permission_granted';

const isPermissionGranted = (permission) => permission?.receive === 'granted';

/**
 * Platform detection helpers
 */
const isNativePlatform = () => {
  try {
    return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

const isBrowserNotificationsAvailable = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

/**
 * Lazy load PushNotifications only on native platforms
 */
let PushNotifications = null;
const ensurePushNotificationsLoaded = async () => {
  if (!PushNotifications) {
    try {
      const mod = await import('@capacitor/push-notifications');
      PushNotifications = mod.PushNotifications;
    } catch (e) {
      console.error('PushNotifications not available:', e);
      return false;
    }
  }
  return !!PushNotifications;
};

export const notificationService = {
  isNative() {
    return isNativePlatform();
  },

  async syncStoredTokenWithBackend() {
    try {
      const authToken = localStorage.getItem('rtb_token');
      if (!authToken) {
        return { success: false, reason: 'no-auth' };
      }

      const pushToken = localStorage.getItem('rtb_push_token');
      if (!pushToken) {
        return { success: false, reason: 'no-token' };
      }

      try {
        await authAPI.updateDeviceToken(pushToken, 'android');
      } catch (authEndpointError) {
        await notificationAPI.registerDeviceToken(pushToken, 'android');
      }
      return { success: true };
    } catch (error) {
      console.error('Failed syncing stored push token with backend:', error);
      return { success: false, reason: 'backend-register-failed', error: error.message };
    }
  },

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    try {
      if (isNativePlatform()) {
        // Native mobile path - use Capacitor
        const loaded = await ensurePushNotificationsLoaded();
        const PN = PushNotifications;
        if (!loaded || !PN) throw new Error('PushNotifications not available');

        await PN.createChannel({
          id: 'default_channel_id',
          name: 'Default Notifications',
          description: 'RTB alerts and updates',
          importance: 4,
          visibility: 1,
          sound: 'default',
        });

        const currentPermission = await PN.checkPermissions();
        if (isPermissionGranted(currentPermission)) {
          localStorage.setItem(NOTIFICATION_PERMISSION_GRANTED_KEY, 'true');
          localStorage.setItem(NOTIFICATION_PERMISSION_KEY, new Date().toISOString());
          await this.registerPushNotifications();
          await this.syncStoredTokenWithBackend();
          return { success: true, permission: currentPermission.receive };
        }

        const permission = await PN.requestPermissions();
        const isGranted = isPermissionGranted(permission);

        if (isGranted) {
          localStorage.setItem(NOTIFICATION_PERMISSION_GRANTED_KEY, 'true');
          localStorage.setItem(NOTIFICATION_PERMISSION_KEY, new Date().toISOString());
          await this.registerPushNotifications();
          await this.syncStoredTokenWithBackend();
          return { success: true, permission: permission.receive };
        } else {
          localStorage.removeItem(NOTIFICATION_PERMISSION_GRANTED_KEY);
          localStorage.setItem(NOTIFICATION_PERMISSION_KEY, new Date().toISOString());
          return {
            success: false,
            permission: permission.receive,
            error: 'Notification permission denied',
            needsSettings: true,
          };
        }
      } else if (isBrowserNotificationsAvailable()) {
        // Web browser path - use Notifications API
        const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
          localStorage.setItem(NOTIFICATION_PERMISSION_GRANTED_KEY, 'true');
          localStorage.setItem(NOTIFICATION_PERMISSION_KEY, new Date().toISOString());
          return { success: true, permission: permission };
        } else {
          localStorage.removeItem(NOTIFICATION_PERMISSION_GRANTED_KEY);
          localStorage.setItem(NOTIFICATION_PERMISSION_KEY, new Date().toISOString());
          return { success: false, permission: permission, error: 'Notification permission denied' };
        }
      } else {
        throw new Error('Notifications not supported on this platform');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if notification permission is granted
   */
  async hasPermission() {
    try {
      if (isNativePlatform()) {
        // Native mobile path
        const loaded = await ensurePushNotificationsLoaded();
        const PN = PushNotifications;
        if (!loaded || !PN) return false;
        
        const permission = await PN.checkPermissions();
        return isPermissionGranted(permission);
      } else if (isBrowserNotificationsAvailable()) {
        // Web browser path
        return Notification.permission === 'granted';
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error checking notification permission:', error);
      return false;
    }
  },

  /**
   * Check if user has already been prompted
   */
  hasBeenPrompted() {
    return !!localStorage.getItem(NOTIFICATION_PERMISSION_KEY);
  },

  /**
   * Register device for push notifications
   */
  async registerPushNotifications() {
    try {
      if (isNativePlatform()) {
        // Native mobile path
        const loaded = await ensurePushNotificationsLoaded();
        const PN = PushNotifications;
        if (!loaded || !PN) throw new Error('PushNotifications not available');

        await PN.createChannel({
          id: 'default_channel_id',
          name: 'Default Notifications',
          description: 'RTB alerts and updates',
          importance: 4,
          visibility: 1,
          sound: 'default',
        });

        const permission = await PN.checkPermissions();
        if (!isPermissionGranted(permission)) {
          return { success: false, error: 'Notification permission not granted' };
        }

        await PN.removeAllListeners();

        let settleRegistration;
        const registrationPromise = new Promise((resolve) => {
          settleRegistration = resolve;
        });
        const registrationTimeout = setTimeout(() => {
          settleRegistration({ success: false, error: 'Push registration timeout' });
        }, 15000);

        // Add listener for registration token
        await PN.addListener('registration', async (token) => {
          try {
            const pushToken = token?.value || token?.token || (typeof token === 'string' ? token : null);
            if (!pushToken) {
              settleRegistration({ success: false, error: 'Empty push token from registration event' });
              return;
            }

            localStorage.setItem('rtb_push_token', pushToken);

            let syncResult = await this.syncStoredTokenWithBackend();
            if (!syncResult.success && syncResult.reason === 'no-auth') {
              await new Promise((resolve) => setTimeout(resolve, 1200));
              syncResult = await this.syncStoredTokenWithBackend();
            }

            if (!syncResult.success) {
              console.warn('Push token saved but backend sync failed:', syncResult);
            }

            settleRegistration({ success: true, synced: !!syncResult.success });
          } catch (error) {
            console.error('Failed to register push token with backend:', error);
            settleRegistration({ success: false, error: error.message || 'Token registration failed' });
          }
        });

        await PN.addListener('registrationError', (error) => {
          console.error('Push registration error:', error);
          settleRegistration({ success: false, error: 'registrationError' });
        });

        // Add listener for received notifications
        await PN.addListener('pushNotificationReceived', (notification) => {
          console.log('Notification received:', notification);
        });

        // Add listener for notification actions
        await PN.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Notification action performed:', notification);
        });

        await PN.register();

        const registrationResult = await registrationPromise;
        clearTimeout(registrationTimeout);

        if (!registrationResult.success) {
          return { success: false, error: registrationResult.error || 'Push registration failed' };
        }

        return { success: true };
      } else if (isBrowserNotificationsAvailable()) {
        // Web browser path - Notifications API
        // Browser notifications are handled via 'new Notification()' calls
        // No explicit registration needed
        return { success: true };
      } else {
        throw new Error('Notifications not supported on this platform');
      }
    } catch (error) {
      console.error('Error registering push notifications:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get notification token for sending push notifications
   */
  async getDeviceToken() {
    try {
      if (isNativePlatform()) {
        return localStorage.getItem('rtb_push_token');
      } else if (isBrowserNotificationsAvailable()) {
        // Web browser path - no device token in Notifications API
        return { web: true, message: 'Web notifications enabled' };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting device token:', error);
      return null;
    }
  },
  /**
   * Send a test notification
   * On web, uses browser Notifications API
   * On native, would need server-side push setup
   */
  async sendTestNotification(title = 'Test Notification', options = {}) {
    try {
      if (!isBrowserNotificationsAvailable()) {
        throw new Error('Notifications not supported on this platform');
      }

      const hasPermission = await this.hasPermission();
      if (!hasPermission) {
        throw new Error('Notification permission not granted');
      }

      // Create and display notification
      const defaultOptions = {
        icon: '/icon.png',
        badge: '/badge.png',
        tag: 'test-notification',
        ...options,
      };

      new Notification(title, defaultOptions);
      return { success: true };
    } catch (error) {
      console.error('Error sending test notification:', error);
      return { success: false, error: error.message };
    }
  },
};

export default notificationService;
